<template>
  <content-subpage
    class="acp-image-generation-subpage"
    :visible="visible"
    :titles="titles"
    :content-title="t('acp_image_content_subpage_title')"
    :has-parent-subpage="isBreadCrumb"
    :back-label="backLabel"
    @content-subpage-hidden="handleCloseSubpage"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <!-- Recipe Selection -->
        <cv-form class="suggested-roles" @submit.prevent>
          <div class="header-description">
            {{ recipeSelectionContent }}
          </div>

          <!-- Error Banner -->
          <cv-inline-notification
            v-if="showErrorBanner"
            :kind="
              getErrorBannerInfo(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE, generationErrorCode)
                .bannerKind
            "
            :title="
              t(
                getErrorBannerInfo(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE, generationErrorCode)
                  .titleKey,
                {
                  feature_name: errorBannerFeatureName,
                },
              )
            "
            low-contrast
            hide-close-button
          >
            <template #subtitle>
              <div>
                {{
                  t(
                    getErrorBannerInfo(
                      INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE,
                      generationErrorCode,
                    ).subtitleKey,
                    {
                      feature_name: errorBannerFeatureName,
                    },
                  )
                }}
              </div>
            </template>
          </cv-inline-notification>

          <!-- No recipes empty state -->
          <empty-placeholder v-if="recipes.length === 0">
            <template #icon>
              <GenericEmpty />
            </template>
            <div v-safe-html="t('acp_generation_content_subpage_no_recipe_message')"></div>
          </empty-placeholder>

          <!-- Recipe Carousel -->
          <carousel v-else :start-index="carouselStartIndex">
            <carousel-item v-for="(recipe, index) in recipes" :key="index">
              <recipe-card
                :id="recipe.id"
                class="acp-image-generation"
                icon="Roles32"
                :title="recipe.title"
                :content="recipe.content"
                :is-selected="selectedRecipeId === recipe.id"
                @on-click="handleRecipeClick"
              />
            </carousel-item>
          </carousel>

          <!-- Instructions Input -->
          <dl class="field-set type-text acp-image-instruction">
            <dt class="field-label">
              {{ t("acp_generation_content_subpage_instructions_message") }}
            </dt>
            <dd class="field-input">
              <rich-text-editor
                ref="instructionsEditorComponent"
                size="extra_large"
                kind="plaintext"
                :placeholder="t('acp_image_content_subpage_instruction_placeholder')"
                :is-mandatory="false"
                :initial-content="instructionsInputValue"
                :content="getCurrentlySelectedRecipeContent()"
                @change="handleInstructionsInput"
              />
            </dd>
          </dl>
        </cv-form>
      </template>
    </template>

    <template v-if="!isLoading" #action-button>
      <fetch-data-button
        class="btn-generate"
        :kind="generateButton.kind"
        v-bind="generateButton"
        :disabled="disabledGenerateImage"
        @click="handleGenerateImage"
        @reset-completed-state="handleGenerateButtonResetCompleted"
        @reset-error-state="handleGenerateButtonResetError"
      >
        <template v-if="generateButton.error">
          <span>{{ t("button_state_error") }}</span>
        </template>
        <template v-else>
          <svg-icon :name="generateButton.iconName" kind="action" />
          <span>{{ t("acp_generation_content_subpage_generate_button_label") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import {
  AcpImageEntryAction,
  AcpImageEntryType,
  GenerateButtonState,
} from "@plugins/authoring-copilot/types.ts";
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from "vue";
import { Recipe } from "@plugins/recipe/types.ts";
import {
  ACP_REQUEST_STATUS,
  INTEGRATOR_APP_FEATURE_CATEGORY,
} from "@modules/app/service/app.constant.ts";
import {
  MAX_INSTRUCTION_LENGTH,
  useAcp,
} from "@plugins/authoring-copilot/authoring-copilot-composable.ts";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import appManagementApi from "@apis/app.management.api.ts";
import { useRecipe } from "@plugins/recipe/recipe-composable.ts";
import { NAMESPACE } from "@modules/editor/service/editor.constant.ts";
import { useStore } from "vuex";
import RandomUtils from "@utils/random.utils.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import richTextUtils from "@plugins/richtext/utils/richtext.utils.ts";
import authoringCopilotApi from "@apis/authoring.copilot.api.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg?component";
import RecipeCard from "@plugins/recipe/components/RecipeCard.vue";
import AcpImageSelectionContentSubpage from "@plugins/authoring-copilot/components/AcpImageSelectionContentSubpage.vue";
import { useApp } from "@plugins/common/composables/app-composable.ts";
import { useErrorBanner } from "@utils/use-error-banner.ts";

interface ContentSubpageParams {
  richTextContext?: string;
  context?: string;
  parentTitle?: string;
  mode: AcpImageEntryType;
  action: AcpImageEntryAction;
}

interface Props {
  params: ContentSubpageParams;
}

const props = defineProps<Props>();
const emit = defineEmits(["image-added"]);

// Data state
const requestId = ref("");
const recipes = ref<Recipe[]>([]);
const isLoading = ref(false);
const instructionsInputValue = ref("");
const selectedRecipeId = ref<number | null>(null);
const hasUpdatedInstructionValue = ref(false);
const showErrorBanner = ref(false);
const generationErrorCode = ref<string | undefined>();
const generateButton = ref<GenerateButtonState>({
  kind: "ai",
  size: "field",
  completed: false,
  error: false,
  loading: false,
  iconOnly: false,
  tipPosition: "top",
  tipAlignment: "end",
  errorMessage: "",
  iconName: "ACPGenerate24",
});
const dirtyId = ref(RandomUtils.randomId());
const carouselStartIndex = ref(0);

const { setDirty, isComponentDirty, removeComponentDirty, promptComponentDirty } =
  useDirty(dirtyId);
const contentSubpageHandler = useContentSubpageInstance();
const { t } = useI18n();
const store = useStore();
const toast = useToast();
const errorHandler = useErrorHandler();
const {
  visible,
  titles,
  setVisible,
  actionHiddenCallBack,
  actionBackCallBack,
  actionHidden,
  actionBack,
  pollForStatus,
  cleanUpPollInstance,
} = useAcp({
  contentSubpageParams: props.params,
  waitModalProcessingTitle: t("acp_image_generation_wait_modal_title"),
  waitModalProcessingContent: t("acp_image_generation_wait_modal_content"),
});
const { getErrorBannerInfo } = useErrorBanner();
const { getAppFeatureId, getAppFeatureName, isAcpImageAppAvailable } = useApp();
const { getStartIndex, sortRecipesByAlphabeticalOrder, setLastUsedRecipeToLocalStorage } =
  useRecipe();

// Computed
const userId = computed(() => store.getters[`${NAMESPACE.AUTH}/userId`]);
const lessonTitle = computed(() => store.getters[`${NAMESPACE.LESSON}/getLessonTitle`]);
const appFeatureId = computed(() => getAppFeatureId(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE));
const errorBannerFeatureName = computed(() =>
  getAppFeatureName(INTEGRATOR_APP_FEATURE_CATEGORY.ACP_IMAGE),
);
const isBreadCrumb = computed(() => {
  return (
    props.params.mode === AcpImageEntryType.COLLECTIBLE ||
    props.params.mode === AcpImageEntryType.GAME_STORY ||
    props.params.mode === AcpImageEntryType.GAMIFICATION
  );
});
const backLabel = computed(() => {
  const editableModes = [AcpImageEntryType.COLLECTIBLE, AcpImageEntryType.GAME_STORY];

  const mode = props.params.mode;
  const action = props.params.action;
  if (editableModes.includes(mode)) {
    const modeLowerCase = mode.toLowerCase();
    const actionLowerCase = action.toLowerCase();
    const key = `acp_image_content_subpage_back_${modeLowerCase}_${actionLowerCase}_title`;

    return t(key) || "";
  }

  switch (mode) {
    case AcpImageEntryType.GAMIFICATION:
      return t("acp_generation_content_subpage_back_to_gamification_title");
    default:
      return "";
  }
});
const recipeSelectionContent = computed(() => {
  const mode = props.params.mode;

  switch (mode) {
    case AcpImageEntryType.COLLECTIBLE:
      return t("acp_image_content_subpage_recipe_selection_collectible_description");
    case AcpImageEntryType.GAME_STORY:
      return t("acp_image_content_subpage_recipe_selection_game_story_description");
    default:
      return t("acp_image_content_subpage_recipe_selection_description");
  }
});
const disabledGenerateImage = computed(() => {
  return isLoading.value || generateButton.value.loading;
});

// Methods
const setBreadcrumbTitle = () => {
  titles.value.push(props.params.parentTitle ?? lessonTitle.value);

  const mode = props.params.mode;
  const action = props.params.action.toLowerCase();

  if (mode === AcpImageEntryType.GAMIFICATION) {
    titles.value.push(t("acp_image_content_subpage_back_gamification_label"));
  }

  const editableModes = [AcpImageEntryType.COLLECTIBLE, AcpImageEntryType.GAME_STORY];
  if (editableModes.includes(mode)) {
    titles.value.push(t("acp_image_content_subpage_back_gamification_label"));

    const key = `gamification_${action}_${mode.toLowerCase()}_label`;
    titles.value.push(t(key) || "");
  }

  titles.value.push(t("acp_image_content_subpage_title"));
};

const fetchData = async () => {
  isLoading.value = true;
  try {
    await fetchRecipes();
    carouselStartIndex.value = getStartIndex(recipes.value);
  } catch (error) {
    errorHandler.passiveApiFailHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const fetchRecipes = async () => {
  if (!isAcpImageAppAvailable.value) {
    return;
  }

  try {
    const response = await appManagementApi.getIntegratorAppFeatureRecipe(appFeatureId.value);

    if (response.code !== "ok") {
      errorHandler.passiveApiFailHandler();
    }

    recipes.value = sortRecipesByAlphabeticalOrder(response.data);
  } catch (error) {
    errorHandler.passiveApiFailHandler(error);
  }
};

const getCurrentlySelectedRecipeContent = () => {
  if (recipes.value.length === 0 || selectedRecipeId.value === null) {
    if (!hasUpdatedInstructionValue.value) {
      return "";
    }
    hasUpdatedInstructionValue.value = false;
    return instructionsInputValue.value;
  }

  hasUpdatedInstructionValue.value = false;
  return recipes.value.find((r) => r.id === selectedRecipeId.value)?.content || "";
};

const handleRecipeClick = (event: { id: number }) => {
  if (selectedRecipeId.value === event.id) {
    // Unselect if already selected
    if (!hasUpdatedInstructionValue.value) {
      resetInstructionInputValue();
    }
    selectedRecipeId.value = null;
  } else {
    selectedRecipeId.value = event.id;
    updateInstructionInputValue(selectedRecipeId.value);
  }
};

const handleInstructionsInput = (value: string) => {
  instructionsInputValue.value = value;
  if (selectedRecipeId.value) {
    hasUpdatedInstructionValue.value = true;
  }
  setDirty(dirtyId.value);
};

const updateInstructionInputValue = (recipeId: number) => {
  const recipe = recipes.value.find((r) => r.id === recipeId);
  if (recipe) {
    instructionsInputValue.value = recipe.content;
    setDirty(dirtyId.value);
  }
};

const resetInstructionInputValue = () => {
  instructionsInputValue.value = "";
  setDirty(dirtyId.value);
};

const validateInstructions = () => {
  const cleanedContent = richTextUtils.removeMediaAndStripHTML(instructionsInputValue.value);
  return cleanedContent.length <= MAX_INSTRUCTION_LENGTH;
};

const handleGenerateImage = async () => {
  if (disabledGenerateImage.value) return;
  generateButton.value.loading = true;
  dismissErrorBanner();

  try {
    // Check app permission
    if (!isAcpImageAppAvailable.value) {
      handleGenerateButtonError();
      return;
    }

    // Validate instructions
    if (!validateInstructions()) {
      handleGenerateButtonError();
      return;
    }

    // save last used recipe
    setLastUsedRecipeToLocalStorage(userId.value, selectedRecipeId.value);

    // Prepare request data
    const requestData = getRequestData();
    const response = await authoringCopilotApi.generateAcpImage(requestData);
    if (response.code !== "ok" || !response.data?.id) {
      showErrorBanner.value = true;
      generationErrorCode.value = response.errorMap?.[0];
      throw new Error("Failed to generate image");
    }

    requestId.value = response.data.id;
    const pollResult = await pollForStatus(requestId.value, authoringCopilotApi.getAcpImageStatus);
    handleGenerationComplete(pollResult);
  } catch (error) {
    handleGenerateButtonError();
    errorHandler.passiveApiFailHandler(error);
  }
};

const getRequestData = () => {
  return {
    rt_context: getContextText(),
    rt_instruction: richTextUtils.removeMediaAndStripHTML(instructionsInputValue.value.trim()),
  };
};

const getContextText = () => {
  const context = props.params.context;
  if (context) {
    switch (props.params.mode) {
      case AcpImageEntryType.COLLECTIBLE:
        return `You are to design one collectible badge with transparent background that fits in a 1:1 aspect ratio based on the following ${richTextUtils.removeMediaAndStripHTML(props.params.context)}`;
      case AcpImageEntryType.GAME_STORY:
        return `Use the following to guide your image generation ${richTextUtils.removeMediaAndStripHTML(context)}`;
      default:
        return "";
    }
  } else {
    return "";
  }
};

const handleGenerationComplete = (pollResult) => {
  removeComponentDirty();
  const status = pollResult?.data?.status;

  switch (status) {
    case ACP_REQUEST_STATUS.SUCCESS:
      const files = pollResult.data?.fileUuids;
      handleGenerateSuccess();
      openSelectionSubpage(files);
      break;
    case ACP_REQUEST_STATUS.NO_RESULT:
      // No result - empty state
      handleGenerateSuccess();
      openSelectionSubpage([]);
      break;
    case ACP_REQUEST_STATUS.ERROR:
      generationErrorCode.value = pollResult?.data?.errorCode;
      showErrorBanner.value = true;
      handleGenerateButtonError();
      break;
  }
};

const openSelectionSubpage = (files) => {
  contentSubpageHandler.show(
    AcpImageSelectionContentSubpage,
    {
      baseTitle: [...titles.value],
      richTextContext: props.params.richTextContext,
      requestId: requestId.value,
      files: files,
      hasParentSubpage: true,
    },
    {
      "image-added": (data) => {
        emit("image-added", data, showAddSuccessToast);
      },
    },
  );
};

const showAddSuccessToast = () => {
  toast.show("success", {
    title: t("acp_image_add_success_toast_title"),
  });
};

const dismissErrorBanner = () => {
  showErrorBanner.value = false;
  generationErrorCode.value = undefined;
};

const handleGenerateSuccess = () => {
  generateButton.value.completed = true;
  generateButton.value.loading = false;
};

const handleGenerateButtonError = () => {
  generateButton.value.loading = false;
  generateButton.value.error = true;
};

const handleGenerateButtonResetCompleted = () => {
  generateButton.value.completed = false;
};

const handleGenerateButtonResetError = () => {
  generateButton.value.error = false;
  generateButton.value.errorMessage = "";
};

const handleCloseSubpage = () => {
  removeComponentDirty();
  actionHidden();
};

onBeforeMount(() => {
  setBreadcrumbTitle();
});

onMounted(async () => {
  await fetchData();
});

onBeforeUnmount(() => {
  cleanUpPollInstance();
});

defineExpose({
  params: props.params,
  setVisible,
  actionHiddenCallBack,
  actionBackCallBack,
  isComponentDirty,
  removeComponentDirty,
  promptComponentDirty,
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.acp-image-generation-subpage {
  .main {
    .main-header {
      margin-bottom: $spacing-02;
    }
  }

  .header-description {
    @include carbon--type-style("body-long-01", true);
  }

  .empty-content {
    text-align: center;

    h4 {
      @include carbon--type-style("productive-heading-02", true);
      margin-bottom: $spacing-03;
      color: $text-01;
    }

    p {
      @include carbon--type-style("body-long-01", true);
      color: $text-02;
    }
  }

  .suggested-roles {
    margin-bottom: $spacing-06;

    .empty-placeholder {
      margin: $spacing-06 0 $spacing-05;

      .content {
        margin-top: 0;
      }
    }

    .acp-image-generation-instruction {
      margin-top: $spacing-05;

      .info {
        @include carbon--type-style("helper-text-01");
      }
    }

    .acp-image-instruction {
      .field-label {
        @include carbon--type-style("body-short-01");
      }
    }
  }

  .knowledge-base {
    .info {
      margin-bottom: $spacing-06;
    }
  }

  .knowledge-base-listing {
    .add-knowledge-base {
      margin-top: $spacing-07;

      .field-input {
        display: flex;
        justify-content: center;
      }
    }
  }

  .rich-text-editor {
    .mce-content-body {
      font-size: inherit;

      &[data-mce-placeholder] {
        white-space: pre-line;
      }
    }
  }

  // Error banner
  .bx--inline-notification--error {
    margin-bottom: $spacing-05;
  }
}
</style>
